/**
 * プロンプト履歴の DB 版 (API) と localStorage 版を統合するユーティリティ。
 *
 * 正本は docs/features/prompt-history-db-plan.md の「入力の正規化」
 * 「フロントエンド: 全画面モーダル + 二層データ」節。マージ規則・正規化規則は
 * バックエンドの RPC 集計キー (SQL 側 BTRIM(..., ' ')) と厳密に一致させる必要が
 * あるため、`.trim()` (タブ・全角スペース等も除去する広い空白除去) は使わない。
 */

import type { PromptHistoryEntry } from './promptHistory';

/** API (`GET /api/prompt-history`) が返す1件分の DB 履歴項目 */
export interface PromptHistoryDbItem {
  prompt: string;
  /** ISO 8601 文字列 */
  last_used_at: string;
  use_count: number;
  possibly_truncated: boolean;
}

/** マージ後の表示用アイテム */
export interface MergedPromptHistoryItem {
  /** 表示用本文 (possibly_truncated の prefix 補完で置換されている場合あり) */
  prompt: string;
  /** 重複判定・検索に使う正規化済み本文 (完全一致キー) */
  normalizedPrompt: string;
  /** 最終使用日時 (epoch ms)。不明な場合は null */
  lastUsedAt: number | null;
  /** 使用回数 (ソート用)。ローカルのみの項目は 1 固定 */
  useCount: number;
  /** false の場合、useCount は実績に基づかない (ローカルのみ)。UI は「—」等で表示する */
  useCountConfirmed: boolean;
  /** DB 側で 4000 字に切り詰められている可能性があるか (ローカル全文へ置換済みなら false) */
  possiblyTruncated: boolean;
  /** データ源 (db=DBのみ / local=ローカルのみ / merged=両方を統合) */
  source: 'db' | 'local' | 'merged';
}

/**
 * プロンプト本文の正規化。
 *
 * 前後の半角スペース (U+0020) の除去 + 改行コードの CRLF/CR → `\n` 統一のみを行う。
 * `.trim()` は使わない (タブ・全角スペース等の除去対象が SQL 側の BTRIM(x, ' ') と
 * ズレるため)。大文字小文字変換・Unicode 正規化も行わない。
 */
export function normalizePromptText(value: string): string {
  return value.replace(/\r\n?/g, '\n').replace(/^ +| +$/g, '');
}

function parseTimestamp(value: string | null | undefined): number | null {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
}

function maxTimestamp(a: number | null, b: number | null): number | null {
  if (a === null) return b;
  if (b === null) return a;
  return Math.max(a, b);
}

/**
 * DB 履歴とローカル履歴をマージする (計画書「マージ規則」節)。
 *
 * - 重複排除は正規化後の完全一致のみ
 * - `possibly_truncated` な DB 項目に対し、その本文を prefix に持つローカル全文が
 *   ちょうど1件のときのみ、表示本文をローカル全文に置換して束ねる。
 *   候補が複数ある場合は束ねず別項目として残す
 * - `last_used_at` は同一本文なら max(DB, local)
 * - `use_count` は DB 値を正。ローカルのみの項目は 1 (useCountConfirmed=false)
 */
export function mergePromptHistory(
  dbItems: PromptHistoryDbItem[],
  localEntries: PromptHistoryEntry[],
): MergedPromptHistoryItem[] {
  const normalizedLocals = localEntries
    .map((entry) => ({ entry, normalized: normalizePromptText(entry.prompt) }))
    .filter((item) => item.normalized.length > 0);

  const consumedLocalIndexes = new Set<number>();
  const results: MergedPromptHistoryItem[] = [];

  for (const dbItem of dbItems) {
    const normalizedDb = normalizePromptText(dbItem.prompt);
    if (!normalizedDb) continue;
    const dbLastUsedAt = parseTimestamp(dbItem.last_used_at);

    // 完全一致するローカル項目があれば日時を統合する
    const exactMatchIndex = normalizedLocals.findIndex(
      (local, idx) => !consumedLocalIndexes.has(idx) && local.normalized === normalizedDb,
    );
    if (exactMatchIndex !== -1) {
      consumedLocalIndexes.add(exactMatchIndex);
      const local = normalizedLocals[exactMatchIndex];
      results.push({
        prompt: dbItem.prompt,
        normalizedPrompt: normalizedDb,
        lastUsedAt: maxTimestamp(dbLastUsedAt, local.entry.savedAt),
        useCount: dbItem.use_count,
        useCountConfirmed: true,
        possiblyTruncated: false, // 完全一致なので切り詰めなしが確定している
        source: 'merged',
      });
      continue;
    }

    // possibly_truncated: prefix 一致するローカル全文がちょうど1件のときだけ束ねる
    if (dbItem.possibly_truncated) {
      const candidates = normalizedLocals
        .map((local, idx) => ({ local, idx }))
        .filter(
          ({ local, idx }) =>
            !consumedLocalIndexes.has(idx) &&
            local.normalized.length > normalizedDb.length &&
            local.normalized.startsWith(normalizedDb),
        );
      if (candidates.length === 1) {
        const { local, idx } = candidates[0];
        consumedLocalIndexes.add(idx);
        results.push({
          prompt: local.entry.prompt,
          normalizedPrompt: local.normalized,
          lastUsedAt: maxTimestamp(dbLastUsedAt, local.entry.savedAt),
          useCount: dbItem.use_count,
          useCountConfirmed: true,
          possiblyTruncated: false, // ローカル全文へ置換済みなので切り詰めなしが確定している
          source: 'merged',
        });
        continue;
      }
      // 候補 0 件 or 複数件 → 束ねず DB 項目のまま (切り詰め済みの可能性を残す)
    }

    results.push({
      prompt: dbItem.prompt,
      normalizedPrompt: normalizedDb,
      lastUsedAt: dbLastUsedAt,
      useCount: dbItem.use_count,
      useCountConfirmed: true,
      possiblyTruncated: dbItem.possibly_truncated,
      source: 'db',
    });
  }

  // 残ったローカルのみの項目
  normalizedLocals.forEach((local, idx) => {
    if (consumedLocalIndexes.has(idx)) return;
    results.push({
      prompt: local.entry.prompt,
      normalizedPrompt: local.normalized,
      lastUsedAt: local.entry.savedAt,
      useCount: 1,
      useCountConfirmed: false,
      possiblyTruncated: false,
      source: 'local',
    });
  });

  return results;
}

export type PromptHistoryPeriodFilter = '7d' | '30d' | 'all';

/** キーワード (本文部分一致) + 期間でクライアント側フィルタする */
export function filterPromptHistory(
  items: MergedPromptHistoryItem[],
  options: { keyword?: string; period?: PromptHistoryPeriodFilter; now?: number },
): MergedPromptHistoryItem[] {
  const keyword = options.keyword?.trim();
  const period = options.period ?? 'all';
  const now = options.now ?? Date.now();
  const periodMs = period === '7d' ? 7 * 24 * 60 * 60 * 1000 : period === '30d' ? 30 * 24 * 60 * 60 * 1000 : null;

  return items.filter((item) => {
    if (keyword && !item.prompt.includes(keyword)) return false;
    if (periodMs !== null) {
      if (item.lastUsedAt === null) return false;
      if (now - item.lastUsedAt > periodMs) return false;
    }
    return true;
  });
}

export type PromptHistorySortKey = 'lastUsedAtDesc' | 'lastUsedAtAsc' | 'useCountDesc';

/** 最終使用日時 (降順/昇順) または使用回数順でソートする (非破壊) */
export function sortPromptHistory(
  items: MergedPromptHistoryItem[],
  sortKey: PromptHistorySortKey,
): MergedPromptHistoryItem[] {
  const sorted = [...items];
  switch (sortKey) {
    case 'lastUsedAtAsc':
      sorted.sort((a, b) => (a.lastUsedAt ?? 0) - (b.lastUsedAt ?? 0));
      break;
    case 'useCountDesc':
      sorted.sort((a, b) => b.useCount - a.useCount || (b.lastUsedAt ?? 0) - (a.lastUsedAt ?? 0));
      break;
    case 'lastUsedAtDesc':
    default:
      sorted.sort((a, b) => (b.lastUsedAt ?? 0) - (a.lastUsedAt ?? 0));
      break;
  }
  return sorted;
}

/**
 * ローカル履歴をシナリオで絞り込む (履歴モーダルの「このシナリオ」)。
 * scenarioId が null なら絞り込まない。scenarioId を記録していない古い項目は
 * 絞り込み時には含めない (どのシナリオで使ったか分からないため。「すべてのシナリオ」では表示される)。
 */
export function filterLocalEntriesByScenario(
  entries: PromptHistoryEntry[],
  scenarioId: string | null,
): PromptHistoryEntry[] {
  if (!scenarioId) return entries;
  return entries.filter((entry) => entry.scenarioId === scenarioId);
}

